import { rangeToHours } from '../../../lib/timeEntryHelpers';
import { getWeekDatesForDate, weekToDateRange } from '../../../lib/utils';
import { AmbiguousWordRule, DayCell, EntryMode, EntryRow } from './interface';

export const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const isWeekend = (i: number) => i === 5 || i === 6; // Fri/Sat — this region's weekend

export function getWeekDates(year: number, week: number): string[] {
  const { from } = weekToDateRange(year, week);
  return getWeekDatesForDate(from);
}

export function formatWeekRange(dates: string[]): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', timeZone: 'UTC' };
  const from = new Date(dates[0] + 'T12:00:00Z').toLocaleDateString('en-GB', opts);
  const to = new Date(dates[6] + 'T12:00:00Z').toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return `${from} – ${to}`;
}

export function formatDayDate(dateStr: string): string {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${d}/${m}`;
}

export const newRow = (): EntryRow => ({
  // Random id, not a counter — a restored draft carries its own rowIds and a
  // counter restarting at 0 would collide with them.
  rowId: crypto.randomUUID(),
  projectId: '',
  phaseId: '',
  days: {},
});

// ─── Unsaved-draft persistence ──────────────────────────────────────────────
// The grid is only sent to the API on "Save Week", so a refresh/tab close
// mid-entry would otherwise lose everything typed.
export const DRAFT_KEY = 'newEntryDraft';

export type Draft = { refDate: string; mode: EntryMode; rows: EntryRow[] };

export function loadDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    const d = raw ? JSON.parse(raw) : null;
    return Array.isArray(d?.rows) && d.rows.length > 0 ? (d as Draft) : null;
  } catch {
    return null; // corrupt/unreadable storage — start clean
  }
}

/** True when anything has actually been typed into the grid (worth saving). */
export function hasDraftContent(rows: EntryRow[]): boolean {
  return rows.some(
    (r) => r.projectId || Object.values(r.days).some((c) => c.hours || c.fromHour || c.toHour || c.notes)
  );
}

export function getCell(row: EntryRow, date: string): DayCell {
  return row.days[date] ?? { hours: '', fromHour: '', toHour: '', notes: '' };
}

// ─── Note guardrails ───────────────────────────────────────────────────────
// Notes are free text, so we normalize before matching: lowercase, strip
// punctuation/hyphens/periods down to spaces, collapse whitespace. This lets
// "internal-meeting", "Internal   Meeting", and "int. meeting" all match
// without also matching unrelated words like "international".
export const INTERNAL_MEETING_ERROR = "Internal meetings belong under Non-Project Related, not a project entry.";
export const PERSONAL_TRAINING_ERROR = "Personal Training belongs under Non-Project Related, not a project entry.";
export const PERSONAL_LEARNING_ERROR = "Personal Learning belongs under Non-Project Related, not a project entry.";

function normalizeNote(note: string): string {
  return note
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function containsInternalMeeting(note: string): boolean {
  const n = normalizeNote(note);
  // Matches "internal meeting(s)" and the abbreviation "int meeting(s)"
  // (e.g. "int. meeting" once punctuation is stripped), with or without a
  // space between the two words ("internalmeeting"), but not "international
  // meeting" since \bint\b requires "int" as its own token.
  return /\b(internal|int)\s?meetings?\b/.test(n);
}

export function containsTraining(note: string): boolean {
  const n = normalizeNote(note);
  return /\btrainings?\b/.test(n);
}

export function containsLearning(note: string): boolean {
  const n = normalizeNote(note);
  return /\blearnings?\b/.test(n);
}

// Checked in order against every project entry's notes on submit; the first
// rule with a match drives the confirmation dialog for that submission.
export const AMBIGUOUS_WORD_RULES: AmbiguousWordRule[] = [
  {
    id: 'training',
    detect: containsTraining,
    question: 'This note mentions "training" on a project entry. Is this model training, or personal training?',
    legitimateLabel: 'Model Training',
    personalLabel: 'Personal Training',
    personalError: PERSONAL_TRAINING_ERROR,
  },
  {
    id: 'learning',
    detect: containsLearning,
    question: 'This note mentions "learning" on a project entry. Is this machine learning, or personal learning?',
    legitimateLabel: 'Machine Learning',
    personalLabel: 'Personal Learning',
    personalError: PERSONAL_LEARNING_ERROR,
  },
];

/** Resolves a cell's effective hours based on the active page-level mode */
export function effectiveHours(cell: DayCell, mode: EntryMode): number {
  if (mode === 'quick') {
    const h = parseFloat(cell.hours);
    return isNaN(h) || h < 0 ? 0 : h;
  }
  // Midnight-aware: toHour <= fromHour is a valid overnight range (e.g. 22:00 -> 02:00),
  // not an invalid one. Only an identical fromHour/toHour means "no range entered."
  if (cell.fromHour && cell.toHour && cell.fromHour !== cell.toHour) {
    return rangeToHours(cell.fromHour, cell.toHour);
  }
  return 0;
}
